import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logoGala from '../assets/common/LogoGala.png';
import FormInput from '../components/FormInput';
import OtpInput from '../components/OtpInput';
import ResendVerificationCode from '../components/ResendVerificationCode';
import { routeForRole } from '../utils/roleRoute';
import { validateEmail } from '../utils/signupValidation';
import {
  clearPendingVerificationEmail,
  readPendingVerificationEmail,
} from '../utils/pendingVerification';
import { clearEmailCodeLink, codeRequestedHere, readEmailCodeLink } from '../utils/emailCodeLink';
import './auth.css';

/**
 * Saisie du code d'activation, atteignable directement depuis le bouton du mail.
 *
 * L'adresse et le code arrivent dans le fragment (`#mail=…&code=…`, cf.
 * utils/emailCodeLink) : un fragment n'est jamais envoyé au serveur, ils ne
 * finissent donc ni dans les journaux d'accès ni dans un Referer. On l'efface de
 * la barre d'URL une fois lu.
 */
const OTP_LENGTH = 6;

const VerifyEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyEmailAndLogin } = useAuth();

  const [link] = useState(readEmailCodeLink);
  const [email, setEmail] = useState(
    () => link.mail || location.state?.email || readPendingVerificationEmail() || '',
  );
  // Figé au montage : recalculé à chaque frappe dans le champ email, il ferait
  // sauter le focus vers les cases du code au premier caractère tapé.
  const [emailKnownOnArrival] = useState(() => !!email);
  const [code, setCode] = useState(link.code);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // StrictMode rejoue les effets : sans ce verrou, le code partirait deux fois.
  const autoSubmitted = useRef(false);

  // `typedCode` : passé par OtpInput.onComplete, l'état `code` n'étant pas encore à jour.
  const handleVerify = async (e, typedCode = code) => {
    e?.preventDefault();
    if (submitting) return;
    setError('');
    const emailError = validateEmail(email);
    if (emailError) { setError(emailError); return; }
    if (typedCode.length !== OTP_LENGTH) {
      setError(`Saisissez les ${OTP_LENGTH} chiffres du code.`);
      return;
    }
    setSubmitting(true);
    try {
      const profile = await verifyEmailAndLogin(email.trim(), typedCode);
      clearPendingVerificationEmail();
      navigate(routeForRole(profile?.role));
    } catch (err) {
      setError(err.message);
      setCode('');
    } finally {
      setSubmitting(false);
    }
  };

  // Ouvert depuis l'e-mail : validé d'office si ce navigateur a demandé le code
  // (inscription ou renvoi), sinon pré-rempli en attendant « Valider ».
  useEffect(() => {
    clearEmailCodeLink();
    if (link.mail && link.code && codeRequestedHere() && !autoSubmitted.current) {
      autoSubmitted.current = true;
      handleVerify(null, link.code);
    }
    // Une seule fois, à l'ouverture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="page">
      <div className="container">
        <div className="signup-logo">
          <img src={logoGala} alt="CycleGala" className="signup-logo-image" />
        </div>

        <h2 className="signup-brand">
          <span className="signup-brand--cycle">Cycle</span>
          <span className="signup-brand--gala">Gala</span>
        </h2>

        <p className="subtitle">
          Saisissez le code à 6 chiffres reçu par email pour activer votre compte.
        </p>

        <form onSubmit={handleVerify} noValidate>
          <FormInput
            label="Email"
            type="email"
            name="email"
            autoComplete="username"
            inputMode="email"
            autoCapitalize="none"
            spellCheck={false}
            placeholder="votre.email@exemple.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          {/* Le focus va au code seulement si l'adresse est déjà connue (lien du mail,
              suite d'inscription) : sinon c'est elle qu'il faut saisir d'abord. */}
          <OtpInput
            label="Code de vérification"
            idPrefix="verify-otp"
            value={code}
            onChange={(v) => { setCode(v); setError(''); }}
            onComplete={(v) => handleVerify(null, v)}
            autoFocus={emailKnownOnArrival}
            disabled={submitting}
            error={!!error}
          />

          {error && <p className="auth-error">{error}</p>}
          {link.code && !error && !submitting && code === link.code && (
            <p className="auth-hint">Code repris de l'e-mail : touchez « Valider » pour activer votre compte.</p>
          )}

          <div className="button-group">
            <button type="submit" className="btn btn--primary" disabled={submitting}>
              {submitting ? 'Vérification…' : 'Valider'}
            </button>
          </div>
        </form>

        <ResendVerificationCode email={email.trim()} onError={setError} />
      </div>
    </div>
  );
};

export default VerifyEmail;
